
'use client';

import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Landmark, ArrowUpRight, ArrowDownLeft, ArrowLeftRight,
  Copy, TrendingUp, LogOut, Check, AlertCircle, Loader2,
  ChevronRight, Wallet, CreditCard, Gamepad2, Users, UserPlus, X as XIcon, FileDown,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose, DialogDescription } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { getAccountByIban, makeTransfer, searchUsersByNickname, updateBankAccountSharing } from '@/lib/actions';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { functions, db } from '@/firebase/config';
import { BankAccount, Transaction } from '@/lib/types';
import { useAuth } from '@/hooks/useAuth';
import { Link } from 'react-router-dom';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

// ─── Credit card ──────────────────────────────────────────────────────────────

const JartiCard = ({ iban, name }: { iban: string; name: string }) => (
  <div
    className="relative h-48 rounded-3xl p-6 text-white flex flex-col justify-between overflow-hidden"
    style={{
      background: 'linear-gradient(135deg, hsl(var(--primary)/0.9) 0%, hsl(var(--primary)/0.4) 60%, hsl(40,60%,30%,0.6) 100%)',
    }}
  >
    <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 80% 20%, white 1px, transparent 1px)', backgroundSize: '32px 32px' }} />
    <div className="relative z-10 flex justify-between items-start">
      <div className="font-black text-lg tracking-tight">JartiBank</div>
      <Landmark className="h-7 w-7 opacity-80" />
    </div>
    <div className="relative z-10 space-y-1">
      <p className="font-mono text-lg tracking-[0.18em] opacity-90">
        {iban.replace(/(.{4})/g, '$1 ').trim()}
      </p>
      <p className="text-sm font-medium uppercase tracking-widest opacity-75">{name}</p>
    </div>
  </div>
);

// ─── Transfer dialog ──────────────────────────────────────────────────────────

const TransferDialog = ({
  open,
  onClose,
  account,
  onSuccess,
}: {
  open: boolean;
  onClose: () => void;
  account: BankAccount;
  onSuccess: () => void;
}) => {
  const [step, setStep] = useState(1);
  const [toIban, setToIban] = useState('');
  const [recipient, setRecipient] = useState<{ accountName: string; iban: string } | null>(null);
  const [amount, setAmount] = useState('');
  const [concept, setConcept] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const { toast } = useToast();

  const reset = () => {
    setStep(1); setToIban(''); setRecipient(null);
    setAmount(''); setConcept(''); setErr('');
  };

  const close = () => { reset(); onClose(); };

  const findRecipient = async () => {
    setLoading(true); setErr('');
    const res = await getAccountByIban(toIban.trim());
    if (res.success && res.account) {
      if (res.account.iban === account.iban) { setErr('No puedes transferirte a ti mismo.'); }
      else { setRecipient(res.account); setStep(2); }
    } else {
      setErr(res.message || 'IBAN no encontrado.');
    }
    setLoading(false);
  };

  const doTransfer = async () => {
    const n = parseFloat(amount);
    if (!n || n <= 0) { setErr('Cantidad no válida.'); return; }
    if (!concept.trim()) { setErr('El concepto no puede estar vacío.'); return; }
    setLoading(true); setErr('');
    const res = await makeTransfer(account.iban, toIban.trim(), n, concept.trim());
    if (res.success) {
      toast({ title: '¡Transferencia enviada!', description: res.message || `${n.toFixed(2)} Jartis enviados a ${recipient?.accountName}.` });
      onSuccess();
      close();
    } else {
      setErr(res.message || 'Error en la transferencia.');
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && close()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {step === 1 ? 'Nueva transferencia' : `Enviar a ${recipient?.accountName}`}
          </DialogTitle>
        </DialogHeader>

        <AnimatePresence mode="wait">
          <motion.div key={step} initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}>
            {step === 1 && (
              <div className="space-y-4 py-4">
                <div className="space-y-1.5">
                  <Label>IBAN del destinatario</Label>
                  <Input
                    placeholder="JA123456789012"
                    value={toIban}
                    onChange={e => setToIban(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && toIban && findRecipient()}
                    className="font-mono"
                  />
                </div>
                {err && <p className="text-sm text-destructive flex items-center gap-1.5"><AlertCircle className="h-4 w-4" />{err}</p>}
              </div>
            )}

            {step === 2 && recipient && (
              <div className="space-y-4 py-4">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/60 border border-border">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Wallet className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{recipient.accountName}</p>
                    <p className="text-xs text-muted-foreground font-mono">{recipient.iban}</p>
                  </div>
                  <Check className="h-5 w-5 text-green-500 shrink-0" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label>Cantidad (Jartis)</Label>
                    <Input type="number" placeholder="0.00" value={amount} onChange={e => setAmount(e.target.value)} min={0.01} step={0.01} />
                    <p className="text-xs text-muted-foreground">Disponible: {account.oro.toLocaleString('es-ES', { minimumFractionDigits: 2 })} J</p>
                  </div>
                  <div className="space-y-1.5">
                    <Label>Concepto</Label>
                    <Input placeholder="Ej: Alquiler" value={concept} onChange={e => setConcept(e.target.value)} />
                  </div>
                </div>
                {err && <p className="text-sm text-destructive flex items-center gap-1.5"><AlertCircle className="h-4 w-4" />{err}</p>}
              </div>
            )}
          </motion.div>
        </AnimatePresence>

        <DialogFooter>
          <Button variant="ghost" onClick={close}>Cancelar</Button>
          {step === 1
            ? <Button onClick={findRecipient} disabled={loading || !toIban.trim()}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Buscar
              </Button>
            : <Button onClick={doTransfer} disabled={loading || !amount || !concept}>
                {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Transferir
              </Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Share access dialog (cuentas secundarias) ───────────────────────────────

const ShareAccessDialog = ({
  open,
  onClose,
  account,
  onUpdated,
}: {
  open: boolean;
  onClose: () => void;
  account: BankAccount;
  onUpdated: (sharedWith: string[]) => void;
}) => {
  const { toast } = useToast();
  const [sharedNames, setSharedNames] = useState<Record<string, string>>({});
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<{ id: string; nickname: string }[]>([]);
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const sharedWith = account.sharedWith || [];

  useEffect(() => {
    if (!open || sharedWith.length === 0) {
      setSharedNames({});
      return;
    }
    const fn = httpsCallable(functions, 'getPublicUserNames');
    fn({ uids: sharedWith }).then((res: any) => setSharedNames(res.data?.names || {})).catch(() => setSharedNames({}));
  }, [open, account.sharedWith]);

  const handleSearch = async (value: string) => {
    setQuery(value);
    if (value.trim().length < 2) { setResults([]); return; }
    const { users } = await searchUsersByNickname(value.trim());
    setResults(users.filter(u => !sharedWith.includes(u.id)));
  };

  const grant = async (uid: string) => {
    setLoadingId(uid);
    const res = await updateBankAccountSharing(account.id, uid, 'add');
    if (res.success) {
      toast({ title: 'Acceso concedido' });
      setQuery(''); setResults([]);
      onUpdated([...sharedWith, uid]);
    } else {
      toast({ title: 'Error', description: res.message, variant: 'destructive' });
    }
    setLoadingId(null);
  };

  const revoke = async (uid: string) => {
    setLoadingId(uid);
    const res = await updateBankAccountSharing(account.id, uid, 'remove');
    if (res.success) {
      toast({ title: 'Acceso revocado' });
      onUpdated(sharedWith.filter(id => id !== uid));
    } else {
      toast({ title: 'Error', description: res.message, variant: 'destructive' });
    }
    setLoadingId(null);
  };

  return (
    <Dialog open={open} onOpenChange={v => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="h-5 w-5 text-primary" /> Compartir cuenta</DialogTitle>
          <DialogDescription>Da acceso a otros miembros de Jartinet para que puedan operar esta cuenta secundaria.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {sharedWith.length > 0 && (
            <div className="space-y-2">
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Con acceso</p>
              {sharedWith.map(uid => (
                <div key={uid} className="flex items-center justify-between gap-2 rounded-xl bg-muted/40 p-2.5">
                  <span className="text-sm font-medium">{sharedNames[uid] || 'Cargando...'}</span>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive/70 hover:text-destructive" onClick={() => revoke(uid)} disabled={loadingId === uid}>
                    {loadingId === uid ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <XIcon className="h-3.5 w-3.5" />}
                  </Button>
                </div>
              ))}
            </div>
          )}

          <div className="space-y-2">
            <Label>Buscar usuario por nickname</Label>
            <Input value={query} onChange={e => handleSearch(e.target.value)} placeholder="Escribe al menos 2 letras..." />
            {results.length > 0 && (
              <div className="space-y-1.5 pt-1">
                {results.map(u => (
                  <div key={u.id} className="flex items-center justify-between gap-2 rounded-xl bg-muted/40 p-2.5">
                    <span className="text-sm font-medium">{u.nickname}</span>
                    <Button size="sm" variant="outline" onClick={() => grant(u.id)} disabled={loadingId === u.id}>
                      {loadingId === u.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
                      Dar acceso
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ─── Transaction item ─────────────────────────────────────────────────────────

const TxItem = ({ tx }: { tx: Transaction }) => {
  const isIn = tx.type === 'deposit' || tx.type === 'transfer_in';
  const peer = isIn ? tx.fromAccountName : tx.toAccountName;
  const label = peer ? (isIn ? `De: ${peer}` : `Para: ${peer}`) : tx.concept;
  const sub = peer ? tx.concept : new Date(tx.date).toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' });
  const isPending = tx.status === 'PENDING';
  const isRejected = tx.status === 'REJECTED';

  return (
    <div className={cn('flex items-center gap-3.5 px-4 py-3 rounded-xl hover:bg-muted/40 transition-colors', isPending && 'opacity-70')}>
      <div className={cn('h-10 w-10 rounded-full flex items-center justify-center shrink-0', isIn ? 'bg-green-500/12 text-green-500' : 'bg-red-500/12 text-red-500')}>
        {isIn ? <ArrowDownLeft className="h-5 w-5" /> : <ArrowUpRight className="h-5 w-5" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate leading-tight flex items-center gap-2">
          {label}
          {isPending && <span className="text-[0.6rem] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-500">Pendiente</span>}
          {isRejected && <span className="text-[0.6rem] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-red-500/15 text-red-500">Rechazada</span>}
        </p>
        <p className="text-xs text-muted-foreground">{sub}</p>
      </div>
      <span className={cn('font-bold text-sm shrink-0', isRejected ? 'text-muted-foreground line-through' : isIn ? 'text-green-500' : 'text-foreground')}>
        {isIn ? '+' : '−'}{tx.amount.toLocaleString('es-ES', { minimumFractionDigits: 2 })} J
      </span>
    </div>
  );
};

// ─── Activity chart ───────────────────────────────────────────────────────────

const ActivityChart = ({ transactions }: { transactions: Transaction[] }) => {
  const data = useMemo(() => {
    // Group last 7 days
    const days: Record<string, { in: number; out: number }> = {};
    const now = new Date();
    for (let i = 6; i >= 0; i--) {
      const d = new Date(now);
      d.setDate(d.getDate() - i);
      const key = d.toLocaleDateString('es-ES', { weekday: 'short' });
      days[key] = { in: 0, out: 0 };
    }
    transactions.forEach(tx => {
      const key = new Date(tx.date).toLocaleDateString('es-ES', { weekday: 'short' });
      if (key in days) {
        if (tx.type === 'deposit' || tx.type === 'transfer_in') days[key].in += tx.amount;
        else days[key].out += tx.amount;
      }
    });
    return Object.entries(days).map(([name, v]) => ({ name, Entradas: v.in, Salidas: v.out }));
  }, [transactions]);

  return (
    <ResponsiveContainer width="100%" height={160}>
      <AreaChart data={data} margin={{ top: 4, right: 4, left: -28, bottom: 0 }}>
        <defs>
          <linearGradient id="gin" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
            <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
          </linearGradient>
          <linearGradient id="gout" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor="hsl(var(--destructive))" stopOpacity={0.25} />
            <stop offset="95%" stopColor="hsl(var(--destructive))" stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border)/0.4)" />
        <XAxis dataKey="name" tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 11 }} axisLine={false} tickLine={false} />
        <YAxis tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 10 }} axisLine={false} tickLine={false} tickFormatter={v => v > 0 ? `${v}` : ''} />
        <Tooltip
          contentStyle={{ background: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: 12, fontSize: 12 }}
          formatter={(v: number, name: string) => [`${v.toFixed(2)} J`, name]}
        />
        <Area type="monotone" dataKey="Entradas" stroke="hsl(var(--primary))" fill="url(#gin)" strokeWidth={1.5} dot={false} />
        <Area type="monotone" dataKey="Salidas" stroke="hsl(var(--destructive))" fill="url(#gout)" strokeWidth={1.5} dot={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
};

// ─── Main export ──────────────────────────────────────────────────────────────

export const BancoClient = ({
  account,
  transactions,
  isLoadingTransactions,
  onTransferSuccess,
  onAccountUpdate,
  onLogout,
}: {
  account: BankAccount;
  transactions: Transaction[];
  isLoadingTransactions: boolean;
  onTransferSuccess: () => void;
  onAccountUpdate: (updates: Partial<BankAccount>) => void;
  onLogout: () => void;
}) => {
  const { toast } = useToast();
  const { user, userData } = useAuth();
  const [transferOpen, setTransferOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [showAllTx, setShowAllTx] = useState(false);

  const isPrincipal = account.accountType === 'principal';
  const isOwner = user?.uid === account.ownerId;

  // Cuentas principales: el saldo lo sincroniza el mod en users/{ownerId}. Lo reflejamos en vivo.
  const [liveBalance, setLiveBalance] = useState<{ oro: number; plata: number; bronce: number } | null>(null);
  useEffect(() => {
    if (!isPrincipal || !account.ownerId) return;
    const unsub = onSnapshot(doc(db, 'users', account.ownerId), (snap) => {
      const d = snap.data();
      if (d) setLiveBalance({ oro: d.oro || 0, plata: d.plata || 0, bronce: d.bronce || 0 });
    });
    return () => unsub();
  }, [isPrincipal, account.ownerId]);

  const displayOro = liveBalance ? liveBalance.oro : account.oro;
  const displayPlata = liveBalance ? liveBalance.plata : (account.plata || 0);
  const displayBronce = liveBalance ? liveBalance.bronce : (account.bronce || 0);

  const stats = useMemo(() => {
    const totalIn = transactions.filter(t => t.type === 'deposit' || t.type === 'transfer_in').reduce((s, t) => s + t.amount, 0);
    const totalOut = transactions.filter(t => t.type === 'withdrawal' || t.type === 'transfer_out').reduce((s, t) => s + t.amount, 0);
    return { totalIn, totalOut };
  }, [transactions]);

  const copyIban = () => {
    navigator.clipboard.writeText(account.iban);
    toast({ title: 'IBAN copiado', description: account.iban });
  };

  /**
   * Extracto en PDF con la imagen de JartiBank. `jspdf` es pesado, así que se
   * carga bajo demanda (import dinámico) y no entra en el bundle inicial.
   */
  const descargarExtracto = async () => {
    try {
      const { generarExtractoPDF } = await import('@/lib/extracto-pdf');
      const nombre = generarExtractoPDF({
        account,
        transactions,
        titular: userData?.nickname || account.accountName,
        saldo: { oro: displayOro || 0, plata: displayPlata || 0, bronce: displayBronce || 0 },
      });
      toast({ title: 'Extracto descargado', description: nombre });
    } catch (e: any) {
      toast({ title: 'Error', description: `No se pudo generar el PDF: ${e.message}`, variant: 'destructive' });
    }
  };

  const visibleTx = showAllTx ? transactions : transactions.slice(0, 8);

  // Las cuentas principales solo gestionan dinero en el juego: sin transferir/invertir/compartir.
  const quickActions: { icon: React.ElementType; label: string; action?: () => void; link?: string }[] = [
    { icon: ArrowLeftRight, label: 'Transferir', action: () => setTransferOpen(true) },
    { icon: Copy, label: 'Copiar IBAN', action: copyIban },
    ...(!isPrincipal ? [{ icon: TrendingUp, label: 'Invertir', link: '/jartinet/banco/inversiones' }] : []),
    ...(!isPrincipal && isOwner ? [{ icon: Users, label: 'Compartir', action: () => setShareOpen(true) }] : []),
  ];
  const colsMap: Record<number, string> = { 1: 'grid-cols-1', 2: 'grid-cols-2', 3: 'grid-cols-3', 4: 'grid-cols-4' };
  const quickActionsColsClass = colsMap[quickActions.length] || 'grid-cols-3';

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="min-h-screen bg-background">
      {/* Top bar */}
      <header className="sticky top-0 z-20 bg-background/80 backdrop-blur border-b border-white/8">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2 font-black text-primary text-lg">
            <Landmark className="h-5 w-5" /> JartiBank
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:block text-sm font-medium text-muted-foreground">{account.accountName}</span>
            <Button variant="ghost" size="sm" onClick={onLogout} className="text-muted-foreground hover:text-foreground">
              <LogOut className="h-4 w-4 mr-1.5" /> Salir
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        {/* Account type banner */}
        <div className={cn(
          'flex flex-wrap items-center gap-2 rounded-2xl border px-4 py-3',
          isPrincipal ? 'border-amber-500/30 bg-amber-500/10' : 'border-sky-500/30 bg-sky-500/10'
        )}>
          {isPrincipal ? <Gamepad2 className="h-4 w-4 flex-shrink-0 text-amber-400" /> : <Users className="h-4 w-4 flex-shrink-0 text-sky-400" />}
          <span className={cn('text-xs font-bold uppercase tracking-widest', isPrincipal ? 'text-amber-400' : 'text-sky-400')}>
            {isPrincipal ? 'Cuenta Principal' : 'Cuenta Secundaria'}
          </span>
          <span className="text-sm text-muted-foreground">
            {isPrincipal ? `Vinculada al UUID: ${account.mcUuid}${account.mcUsername ? ` (${account.mcUsername})` : ''}` : account.accountName}
          </span>
        </div>

        {/* Balance + Card row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Balance */}
          <div className="bg-card/50 backdrop-blur border border-white/8 rounded-3xl p-6 space-y-4">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">
              Saldo disponible{isPrincipal && <span className="ml-2 normal-case tracking-normal text-amber-400/80">· sincronizado desde Minecraft</span>}
            </p>
            <div>
              <span className="text-4xl font-black tabular-nums">
                {displayOro.toLocaleString('es-ES', { minimumFractionDigits: 2 })}
              </span>
              <span className="text-primary font-bold text-xl ml-2">Oro</span>
            </div>
            <div className="flex gap-4 text-sm text-muted-foreground">
              <span>{displayPlata.toLocaleString('es-ES', { minimumFractionDigits: 2 })} Plata</span>
              <span>{displayBronce.toLocaleString('es-ES', { minimumFractionDigits: 2 })} Bronce</span>
            </div>
            <div className="grid grid-cols-2 gap-3 pt-1">
              <div className="p-3 rounded-2xl bg-green-500/8 border border-green-500/20">
                <p className="text-xs text-green-400 mb-0.5">Entradas</p>
                <p className="font-bold text-green-400 text-sm">{stats.totalIn.toLocaleString('es-ES', { minimumFractionDigits: 2 })} J</p>
              </div>
              <div className="p-3 rounded-2xl bg-red-500/8 border border-red-500/20">
                <p className="text-xs text-red-400 mb-0.5">Salidas</p>
                <p className="font-bold text-red-400 text-sm">{stats.totalOut.toLocaleString('es-ES', { minimumFractionDigits: 2 })} J</p>
              </div>
            </div>
          </div>

          {/* Card visual */}
          <div className="space-y-4">
            <JartiCard iban={account.iban} name={account.accountName} />
          </div>
        </div>

        {/* Quick actions */}
        <div className={cn('grid gap-3', quickActionsColsClass)}>
          {quickActions.map(({ icon: Icon, label, action, link }) => (
            link
              ? <Link key={label} to={link} state={{ account }} className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-card/50 border border-white/8 hover:bg-card/70 hover:border-primary/30 transition-all group">
                  <div className="p-3 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <span className="text-xs font-semibold text-muted-foreground group-hover:text-foreground transition-colors">{label}</span>
                </Link>
              : <button key={label} onClick={action} className="flex flex-col items-center gap-2 p-4 rounded-2xl bg-card/50 border border-white/8 hover:bg-card/70 hover:border-primary/30 transition-all group">
                  <div className="p-3 rounded-xl bg-primary/10 group-hover:bg-primary/20 transition-colors">
                    <Icon className="h-5 w-5 text-primary" />
                  </div>
                  <span className="text-xs font-semibold text-muted-foreground group-hover:text-foreground transition-colors">{label}</span>
                </button>
          ))}
        </div>

        {/* Chart + Transactions */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          {/* Transactions */}
          <div className="lg:col-span-3 bg-card/50 backdrop-blur border border-white/8 rounded-3xl overflow-hidden">
            <div className="flex items-center justify-between gap-2 px-5 pt-5 pb-3">
              <h3 className="font-bold">Actividad reciente</h3>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={descargarExtracto}
                  disabled={transactions.length === 0}
                  title="Descargar el extracto en PDF"
                >
                  <FileDown className="mr-1 h-3.5 w-3.5" />
                  Extracto PDF
                </Button>
                {transactions.length > 8 && (
                  <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => setShowAllTx(v => !v)}>
                    {showAllTx ? 'Ver menos' : `Ver todas (${transactions.length})`}
                    <ChevronRight className={cn('h-3.5 w-3.5 ml-1 transition-transform', showAllTx && 'rotate-90')} />
                  </Button>
                )}
              </div>
            </div>

            {isLoadingTransactions ? (
              <div className="px-4 pb-4 space-y-2">
                {[1, 2, 3].map(i => <Skeleton key={i} className="h-14 rounded-xl" />)}
              </div>
            ) : transactions.length === 0 ? (
              <div className="px-5 pb-8 text-center text-muted-foreground text-sm pt-4">
                <CreditCard className="h-10 w-10 mx-auto mb-2 opacity-30" />
                <p>No hay transacciones todavía.</p>
              </div>
            ) : (
              <ScrollArea className={cn(showAllTx ? 'h-[500px]' : 'h-auto')}>
                <div className="px-2 pb-3">
                  {visibleTx.map(tx => <TxItem key={tx.id} tx={tx} />)}
                </div>
              </ScrollArea>
            )}
          </div>

          {/* Chart */}
          <div className="lg:col-span-2 bg-card/50 backdrop-blur border border-white/8 rounded-3xl p-5 space-y-3">
            <h3 className="font-bold text-sm">Últimos 7 días</h3>
            {transactions.length > 0
              ? <ActivityChart transactions={transactions} />
              : <div className="h-40 flex items-center justify-center text-muted-foreground text-sm">Sin datos</div>}
            <div className="flex gap-4 text-xs text-muted-foreground">
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-primary inline-block" />Entradas</span>
              <span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-destructive inline-block" />Salidas</span>
            </div>
          </div>
        </div>
      </main>

      <TransferDialog
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        account={account}
        onSuccess={onTransferSuccess}
      />
      <ShareAccessDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        account={account}
        onUpdated={(sharedWith) => onAccountUpdate({ sharedWith })}
      />
    </motion.div>
  );
};
